import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Paper, Typography, Box } from '@mui/material';
import FeatureErrorBoundary from 'components/errorBoundary/FeatureErrorBoundary';
import { IncidentsTab } from './components/incidents/IncidentsTab';
import IncidentDetail from './components/IncidentDetail/IncidentDetail';
import { useCANvisionContext } from './context/useCANvisionContext';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse the `incidentId` query param into a number or null.
 * Handles missing, empty, non-numeric, NaN, and negative values gracefully.
 */
function parseIncidentIdParam(value: string | null): number | null {
  if (value == null || value === '') return null;
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed <= 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// CANvisionIncidentsPage
// ---------------------------------------------------------------------------

/**
 * Standalone full-page view for the Incidents tab.
 *
 * Route: /canvision/incidents
 *
 * Supports the `?incidentId={id}` query parameter to auto-open the
 * IncidentDetail drawer on mount (used by the Guard Dashboard's
 * "View in CANvision" action).
 *
 * The page reuses `<IncidentsTab>` from the dashboard without modification.
 * When the `incidentId` query param is present, a separate IncidentDetail
 * drawer is rendered at the page level (IncidentsTab also has its own
 * internal detail drawer for table row clicks).
 */
function CANvisionIncidentsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { cameras, sites } = useCANvisionContext();

  // -------------------------------------------------------------------------
  // Query param: ?incidentId
  // -------------------------------------------------------------------------

  const initialIncidentId = useMemo(
    () => parseIncidentIdParam(searchParams.get('incidentId')),
    // Only parse once from initial URL — intentionally omit searchParams
    // from deps so closing the drawer doesn't re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [deepLinkIncidentId, setDeepLinkIncidentId] = React.useState<number | null>(initialIncidentId);

  const handleCloseDeepLink = useCallback(() => {
    setDeepLinkIncidentId(null);
    // Remove the query param from the URL without a navigation
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('incidentId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // -------------------------------------------------------------------------
  // Derive site IDs for IncidentsTab
  // -------------------------------------------------------------------------

  const siteIds = useMemo(
    () => (sites ?? []).map((s) => s.id).filter(Boolean),
    [sites],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <FeatureErrorBoundary featureName="CANvision Incidents">
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        {/* Page heading */}
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            Incidents
          </Typography>
        </Box>

        {/* Incidents tab content */}
        <Box sx={{ flex: 1, px: { xs: 1, sm: 2 }, pb: 2, overflow: 'auto' }}>
          <IncidentsTab
            cameras={cameras ?? []}
            siteIds={siteIds}
          />
        </Box>
      </Paper>

      {/* Deep-link incident detail drawer (from ?incidentId query param) */}
      {deepLinkIncidentId != null && (
        <IncidentDetail
          incidentId={deepLinkIncidentId}
          open={deepLinkIncidentId != null}
          onClose={handleCloseDeepLink}
        />
      )}
    </FeatureErrorBoundary>
  );
}

export default CANvisionIncidentsPage;
